'use client';
import { type EmblaCarouselType } from 'embla-carousel';
import { motion } from 'motion/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { Button, type ButtonProps } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Tabs } from '@/app/components/navigation/tabs';
import { PartItem } from '@/app/components/sections/service/parts/part-item';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { easeBounce } from '@/app/constants/motion';
import { useInView } from '@/app/hooks/useInView';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { scrollToElementPromise } from '@/app/utils/scroll-to-promise';
import { type CvPartConfig, type LcvPartConfig } from '@/types/tis-cms/types';

export interface RecommendedPartsProps {
  lead: string;
  title: string;
  description: string;
  buttons: ButtonProps[];
  cardUrl?: string;
  categories?: CvPartConfig['categories'] | LcvPartConfig['categories'] | null;
  testId?: string;
}
export const RecommendedParts = ({
  lead,
  title,
  description,
  cardUrl,
  categories = [],
  buttons,
  testId = 'recommended-parts',
}: RecommendedPartsProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const ref = useRef<HTMLDivElement>(null);
  const [isParamChange, setIsParamChange] = useState<boolean>(false);
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const { selectedIndex, onPageChange } = usePaginationSlider(emblaApi);
  const tabRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref);

  const tabItems = categories?.map((o, i) => ({
    label: o?.partCategory?.title || '',
    value: `${i}`,
  }));

  const handleSetTabIndex = (index: number) => {
    onPageChange(index + 1);
  };

  useEffect(() => {
    if (!emblaApi || !ref.current) return;

    const productsParam = searchParams?.get('products');
    if (!productsParam) return;

    const matchIndex =
      categories?.findIndex((c) => c?.partCategory?.url === productsParam) ??
      -1;

    if (matchIndex >= 0) {
      onPageChange(matchIndex + 1);

      if (isInView === false) {
        scrollToElementPromise(ref.current, {
          offset: -56,
          checkScrollEnd: true,
        }).then(() => {
          setIsParamChange(true);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, searchParams]);

  useEffect(() => {
    if (!isInView) return;

    const group = categories?.[selectedIndex];
    if (group?.partCategory?.url) {
      const params = new URLSearchParams(searchParams?.toString() || '');
      params.set('products', group.partCategory.url);
      window.history.replaceState(null, '', `${pathname}?${params.toString()}`);
      setIsParamChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  if (!categories || !tabItems || tabItems?.length === 0) return;
  return (
    <Section
      ref={ref}
      size="md"
      {...createDataAttributes({ test: `${testId}-section` })}
      className="from-silver-100 bg-linear-180 to-white"
    >
      <Container
        className="mb-7 flex flex-col lg:mb-10 lg:flex-row"
        {...createDataAttributes({ test: `${testId}-container` })}
      >
        <HeaderLevel2
          title={title}
          lead={lead}
          content2ClassName="mb-4"
          content1={{
            content: (
              <div className="flex-1 space-y-6">
                <p
                  className="text-body-1"
                  dangerouslySetInnerHTML={{ __html: description }}
                  {...createDataAttributes({ test: `heading-description` })}
                />
                <ButtonGroup>
                  {buttons.map((button, index) => (
                    <Button
                      key={index}
                      {...button}
                      className="w-full md:w-auto"
                      {...createDataAttributes({
                        test: `heading-button-${index}`,
                      })}
                      {...createDataGA({
                        click: true,
                        eventName: `${testId}`,
                        group: 'heading-cta',
                        url: button.href,
                        text: button.children,
                      })}
                    />
                  ))}
                </ButtonGroup>
              </div>
            ),
          }}
        />
      </Container>
      <Container size="desktop-only" className="mb-6 md:px-10 lg:mb-10">
        <Tabs
          ref={tabRef}
          items={tabItems ?? []}
          variant="underline"
          className="w-fit text-gray-700 data-selected:text-red-500"
          wrapperClassName="w-fit scrollbar-hidden flex-nowrap overflow-auto"
          scrollToSelectedTab={!!isInView && !!isParamChange}
          indicatorComponent={
            <motion.div
              className="absolute bottom-0 left-0 z-10 h-0.5 w-full bg-red-500"
              layoutId="filled-indicator"
              transition={{
                duration: 0.5,
                ease: easeBounce,
              }}
            />
          }
          value={`${selectedIndex}`}
          onChange={(_, index) => handleSetTabIndex(index)}
          tracking={{
            eventName: `${testId}`,
            group: `${testId}`,
          }}
        />
      </Container>
      <Container>
        <Sliders
          onEmblaInit={setEmblaApi}
          emblaOptions={{ containScroll: false }}
          {...createDataAttributes({
            test: `${testId}-items`,
          })}
        >
          {categories?.map((category, i) => (
            <div
              key={i}
              className="mr-4 h-fit shrink-0 basis-full md:mr-6"
              {...createDataAttributes({
                test: `${testId}-parts-group-${i}`,
              })}
            >
              <div
                key={i}
                className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 xl:grid-cols-4"
              >
                {category?.partItems?.map((item, j) => (
                  <PartItem
                    key={j}
                    index={j}
                    {...item}
                    url={`${cardUrl}/${item?.url}`}
                    testId="recommended-parts"
                    dataTrack="recommended-parts"
                    className={cn({
                      hidden: j > 1 && i !== selectedIndex,
                    })}
                  />
                ))}
              </div>
            </div>
          ))}
        </Sliders>
      </Container>
    </Section>
  );
};
